import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { PlusIcon } from 'lucide-react';

import { EmptyList } from '@components/EmptyList';
import { PageTitle } from '@components/PageTitle';
import { Anchor } from '@components/ui/anchor';
import { Pagination } from '@components/ui/pagination';
import { adminRoleNames } from '@constants/admins.constant';
import { queryKeys } from '@constants/queryKeys.constant';
import { EStaleTime } from '@enums/staleTime.enum';
import { formatFullName } from '@formatters/fullName.formatter';
import {
  getAllAdminsSchema,
  type GetAllAdminsParams,
} from '@services/admins/dtos/getAll.dto';
import { adminService } from '@services/admins/service';

import { AdminFilters } from './@components/Filters';
import { AdminTableCell } from './@components/TableCell';

function adminsQueryOptions(params: GetAllAdminsParams) {
  return queryOptions({
    staleTime: EStaleTime.OneHour,
    queryKey: queryKeys.admins.list(params),
    queryFn: () => adminService.getAll(params),
  });
}

export const Route = createFileRoute('/_layout/(private)/admins/')({
  component: Admins,
  validateSearch: search => getAllAdminsSchema.parse(search),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    const options = adminsQueryOptions(deps);
    return context.queryClient.ensureQueryData({
      ...options,
      revalidateIfStale: true,
    });
  },
  pendingMs: 300,
  pendingMinMs: 600,
});

function Admins() {
  const searchParams = Route.useSearch();
  const { data } = useSuspenseQuery(adminsQueryOptions(searchParams));
  const hasFilters = Object.values({
    archived: searchParams.archived,
    roles: searchParams.roles?.length ? searchParams.roles : undefined,
    search: searchParams.search || undefined,
  }).some(value => value !== undefined);

  return (
    <div className="relative">
      <PageTitle title="Admins" />

      <AdminFilters />

      <div
        className="bg-background dark:border-primary-light-one mb-2 grid w-full overflow-hidden rounded-xl border border-zinc-200 shadow"
        role="table"
      >
        <div
          className="bg-primary-light-two dark:text-primary-dark-one grid h-14 grid-cols-7 items-center font-medium text-zinc-600"
          role="row"
        >
          {headers.map(header => (
            <span
              className="text-center text-sm font-medium"
              key={header.accessorKey}
              role="columnheader"
            >
              {header.title}
            </span>
          ))}
        </div>

        {data.list.map(row => (
          <Link
            className="bg-row-odd even:bg-row-even hover:bg-row-odd-hover even:hover:bg-row-even-hover grid min-h-12 grid-cols-7 items-center transition-all duration-200"
            key={row.id}
            params={{ id: row.id }}
            role="row"
            to="/admins/$id/update"
          >
            <AdminTableCell type="id" value={row.displayId} />
            <AdminTableCell type="date" value={row.createdAt} />
            <AdminTableCell
              value={formatFullName({
                firstName: row.firstName,
                lastName: row.lastName,
              })}
            />
            <AdminTableCell value={adminRoleNames[row.role]} />
            <AdminTableCell value={row.email} />
            <AdminTableCell type="date" value={row.lastLogin ?? ''} />
            <AdminTableCell type="tag" value={row.archived} />
          </Link>
        ))}

        <EmptyList
          alt="No admins animation/image"
          description={
            hasFilters
              ? 'Try adjusting your search or filters'
              : 'Click the + button to create an admin'
          }
          isVisible={!data.list.length}
          title={hasFilters ? 'No matching admins.' : 'No admins yet.'}
        />
      </div>

      <div className="ml-auto flex w-fit items-center">
        <span className="mr-4 text-sm text-gray-500 dark:text-gray-300">
          Total: {data.count || 0} admin(s)
        </span>
        <Pagination
          currentPage={data.pagination.page}
          totalPages={data.pagination.totalPages}
        />
      </div>

      <Anchor
        className="size-12"
        float
        rounded="full"
        size="icon"
        to="/admins/create"
        variant="primary"
      >
        <PlusIcon size={24} />
      </Anchor>
    </div>
  );
}

const headers = [
  { accessorKey: 'displayId', title: 'ID' },
  { accessorKey: 'createdAt', title: 'Creation date' },
  { accessorKey: 'name', title: 'Name' },
  { accessorKey: 'role', title: 'Role' },
  { accessorKey: 'email', title: 'Email' },
  { accessorKey: 'lastLogin', title: 'Last connection' },
  { accessorKey: 'status', title: 'Status' },
];
